import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ChannelService } from '@service/channel.service';
import { GraphsService } from '@service/graphs.service';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { ExhaustGraphComponent } from './exhaust-graph/exhaust-graph.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomGraphService } from '@service/custom-graph.service';
import { CustomGraph } from 'ops/common/models/custom-graph';
import { CustomGraphAccessSettingsService } from '@service/custom-graph-access-settings.service';
import { ResponsiveService } from '@service/responsive.service';
import { AuthService } from '@service/auth.service';
import { AccessType, PageAccessType } from 'ops/common/models/access-settings';
import { CustomGraphType } from '@models/custom-graph';
import { CustomGraphSocketService } from '@service/custom-graph-socket.service';
import { ExhaustGraph, Graph, GraphType } from 'ops/common/models/graphs';
import { Channel } from 'ops/common/models/channel';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import domtoimage from 'dom-to-image';

@Component({
    selector: 'app-ops-graphs',
    templateUrl: './ops-graphs.component.html',
    styleUrls: ['./ops-graphs.component.scss'],
    standalone: false,
})
export class OpsGraphsComponent implements OnInit {
    readonly GAUGE = CustomGraphType.gauge;
    readonly METER = CustomGraphType.meter;
    readonly INDICATOR = CustomGraphType.indicator;

    graphList: (Graph | CustomGraph)[] = [];
    filteredGraphList: (Graph | CustomGraph)[] = [];
    channels: Channel[] = [];
    channelObj = {};
    selectedGraph: Graph | CustomGraph;
    shareSettingsOpened: boolean = false;

    exhaustTabArr = [];

    usersAccess = [];
    viewInit: boolean = false;
    isLoadData: boolean = false;

    graphAccessType: string;
    graphAccessIcon: string;
    graphAccessTypeTile: string = 'page-access.type';

    readonly BAR_GRAPH = GraphType.Bar;
    readonly EXHAUST_GRAPH = GraphType.Exhaust;
    readonly GAUGE_GRAPH = GraphType.Gauge;

    @ViewChild(ExhaustGraphComponent) exhaust!: ExhaustGraphComponent;
    @ViewChild('header') header!: ElementRef;
    @ViewChild('mainContent', { static: true }) mainContent!: ElementRef;
    @ViewChild('deleteConfirmationOverlay') deleteConfirmationOverlay: ConfirmationWindowComponent;
    @ViewChild('customGraphContainer') customGraphContainer: ElementRef;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private graphsService: GraphsService,
        private channelService: ChannelService,
        private customGraphService: CustomGraphService,
        private customGraphSocketService: CustomGraphSocketService,
        private authService: AuthService,
        private router: Router,
        private customGraphAccessService: CustomGraphAccessSettingsService,
        public responsive: ResponsiveService,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit(): Promise<void> {
        this.customGraphSocketService.startSubscriptions();
        const currentCustomGraphId = this.route.snapshot.paramMap.get('customGraphId');
        await this.loadGraphData(currentCustomGraphId);

        this.isLoadData = true;

        this.route.paramMap.subscribe(async (params) => {
            const currentCustomGraphId = params.get('customGraphId');
            if (currentCustomGraphId && this.isLoadData) {
                if (this.selectedGraph?.id == currentCustomGraphId) return;
                this.viewInit = false;
                await this.loadGraphData(currentCustomGraphId);
            }
        });

        this.customGraphSocketService
            .updateListSubject()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async () => {
                const currentCustomGraphId = this.route.snapshot.paramMap.get('customGraphId');
                await this.loadGraphData(currentCustomGraphId);
            });
    }

    ngAfterViewInit(): void {
        this.authService.currentUser$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async () => {
                const currentCustomGraphId = this.route.snapshot.paramMap.get('customGraphId');
                if (this.deleteConfirmationOverlay?.style.display != 'none')
                    this.deleteConfirmationOverlay.emitValue(false);
                if (currentCustomGraphId && this.isLoadData) {
                    this.viewInit = false;
                    await this.loadGraphData(currentCustomGraphId);
                }
            });
    }

    ngOnDestroy(): void {
        this.customGraphSocketService.destroySubscriptions();
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async loadGraphData(customGraphId) {
        const [graphs, channels] = await Promise.all([
            this.graphsService.getAll(),
            lastValueFrom(this.channelService.getChannels()),
        ]);
        this.channels = channels;
        this.createChannelObj();

        const customGraphList = await this.customGraphService.getList();
        this.graphList = [...graphs];
        for (const customGraph of customGraphList) {
            this.graphList.push({
                ...customGraph,
            });
        }

        this.selectedGraph = await this.customGraphService.getSelectedGraph(customGraphId);
        if (this.isCustomGraphType(this.selectedGraph)) {
            this.selectedGraph = this.selectedGraph;
            this.usersAccess = await this.customGraphAccessService.getAll(this.selectedGraph.id);
        } else {
            this.selectedGraph = null;
            this.usersAccess = [];
        }
        this.updatePageAccessType();

        await this.setCustomGraphList();
        this.cdr.detectChanges();
    }

    createChannelObj() {
        for (let i = 0; i < this.channels.length; i++) {
            this.channelObj[this.channels[i].fcuData.fcuChannelId] = this.channels[i];
        }
    }

    async selectGraph(customGraphId) {
        const selectedGraph = this.graphList.find((graph) => graph.id === customGraphId);
        if (this.isCustomGraphType(selectedGraph)) {
            this.router.navigate(['/ops', 'graphs', customGraphId]);
        } else {
            this.selectedGraph = selectedGraph;
            this.setExhaustTabArr();
        }
        this.updatePageAccessType();
        this.cdr.detectChanges();
    }

    setExhaustTabArr() {
        this.exhaustTabArr = [];

        if (!this.selectedGraph || !this.selectedGraph[this.EXHAUST_GRAPH]) return;
        let graph = this.selectedGraph[this.EXHAUST_GRAPH] as ExhaustGraph;

        if (graph.exhaustChannels && graph?.exhaustChannels.length > 0) {
            this.exhaustTabArr.push({ type: ExhaustGraphType.Cylinder });
            this.exhaustTabArr.push({ type: ExhaustGraphType.Devitaion });
        }

        if (graph.turboChargerChannels && graph.turboChargerChannels.length > 0) {
            this.exhaustTabArr.push({ type: ExhaustGraphType.Turbo });
        }
    }

    scrollToClickedSetion(type: string, i) {
        let element = this.getExhaustElement(type);
        if (!element) return '';
        const rect = element.nativeElement.getBoundingClientRect();
        this.mainContent.nativeElement.scrollTo({
            top: (rect.bottom - rect.top) * i,
            behavior: 'smooth',
        });
    }

    checkIfSectionInView(type: string) {
        let element = this.getExhaustElement(type);
        if (!element) return '';
        const rect = element.nativeElement.getBoundingClientRect();
        let offset = this.header.nativeElement.clientHeight;

        let isVisible = rect.top - offset < rect.bottom - rect.top && rect.top - offset > 0;

        return isVisible;
    }

    getExhaustElement(type: string) {
        if (type === ExhaustGraphType.Cylinder) {
            return this.exhaust?.cylinder;
        } else if (type === ExhaustGraphType.Devitaion) {
            return this.exhaust?.deviation;
        } else {
            return this.exhaust?.turbo;
        }
    }

    getHeight() {
        return window.innerHeight - this.header?.nativeElement.clientHeight - 65;
    }

    createCustomGraph() {
        this.goCreatePage();
    }

    isCustomGraphType(obj: any): obj is CustomGraph {
        return typeof obj?.displayName === 'string';
    }

    isCustomGraphTypeGauge(customGraph) {
        return customGraph?.type === this.GAUGE;
    }

    isCustomGraphTypeMeter(customGraph) {
        return customGraph?.type === this.METER;
    }

    isCustomGraphTypeIndicator(customGraph) {
        return customGraph?.type === this.INDICATOR;
    }

    async changeShareSettings() {
        this.usersAccess = await this.customGraphAccessService.getAll(this.selectedGraph.id);
        this.shareSettingsOpened = !this.shareSettingsOpened;
    }

    async updateUsersAccess(updatedUsersAccess) {
        await this.customGraphAccessService.update([...updatedUsersAccess]);
        this.router.navigate(['/ops', 'graphs', this.selectedGraph.id]);
    }

    async updatePageAccess(pageAccess) {
        await this.customGraphAccessService.updatePageAccess(pageAccess, this.selectedGraph.id);
        this.router.navigate(['/ops', 'graphs', this.selectedGraph.id]);
    }

    async setCustomGraphList() {
        const currentUserId = this.authService.getCurrentUserId();
        this.filteredGraphList = Object.assign([]);
        if (!currentUserId) {
            this.graphList.forEach((customGraph) => {
                if (this.isCustomGraphType(customGraph) && customGraph.publicAccess) {
                    this.filteredGraphList.push(customGraph);
                } else if (!this.isCustomGraphType(customGraph)) {
                    this.filteredGraphList.push(customGraph);
                }
            });
        } else {
            for (let i = 0; i < this.graphList.length; i++) {
                let graph = this.graphList[i];
                if (this.isCustomGraphType(graph) && graph.publicAccess) {
                    this.filteredGraphList.push(graph);
                } else if (this.isCustomGraphType(graph)) {
                    const accessLevel = await this.customGraphAccessService.getAll(graph.id);
                    accessLevel.forEach((usersAccess) => {
                        if (
                            usersAccess.roleId === currentUserId &&
                            usersAccess.accessLevel !== AccessType.NoAccess
                        ) {
                            this.filteredGraphList.push(graph);
                        }
                    });
                } else if (!this.isCustomGraphType(graph)) {
                    this.filteredGraphList.push(graph);
                }
            }
        }

        await this.redirectPage();
    }

    async redirectPage() {
        if (this.filteredGraphList.length === 0) {
            await this.goCreatePage();
            return;
        }
        const firstCustomGraph = this.filteredGraphList[0];
        if (!this.selectedGraph) {
            if (firstCustomGraph) {
                if (!this.isCustomGraphType(firstCustomGraph)) {
                    this.selectedGraph = firstCustomGraph;
                } else {
                    await this.redirectCustomGraph();
                }
            }
        } else {
            const findSelectedCustomGraph = this.filteredGraphList.find(
                (customGraph) => customGraph.id === this.selectedGraph.id,
            );
            if (!findSelectedCustomGraph) {
                if (!this.isCustomGraphType(firstCustomGraph)) {
                    this.selectedGraph = firstCustomGraph;
                } else {
                    await this.redirectCustomGraph();
                }
            }
        }
        this.updatePageAccessType();
        this.viewInit = true;
    }

    async redirectCustomGraph() {
        const customGraphId = this.route.snapshot.paramMap.get('customGraphId') || '';
        if (customGraphId) {
            const selectedGraph = this.filteredGraphList.find(
                (graph) => graph.id === customGraphId,
            );
            if (selectedGraph) {
                await this.router.navigate(['/ops', 'graphs', customGraphId]);
            } else {
                if (this.filteredGraphList.length === 0) {
                    await this.goCreatePage();
                } else {
                    await this.router.navigate(['/ops', 'graphs', this.filteredGraphList[0].id]);
                }
            }
        } else {
            await this.router.navigate(['/ops', 'graphs', this.filteredGraphList[0].id]);
        }
    }

    customGraphEdit(customGraph: CustomGraph) {
        this.router.navigate(['/ops', 'graphs', 'create', `${customGraph.id}`]);
    }

    async customDelete() {
        this.deleteConfirmationOverlay.setVisible(true);
        const onDeleteConfirm = this.deleteConfirmationOverlay.onOptionSelected.subscribe(
            async (v: boolean) => {
                onDeleteConfirm.unsubscribe();
                if (v) {
                    this.viewInit = false;
                    await this.customGraphService.delete(this.selectedGraph.id);
                    await this.loadGraphData('');
                }
            },
        );
    }

    async goCreatePage() {
        await this.router.navigate(['/ops', 'graphs', 'create']);
    }

    graphHasData() {
        return false;
    }

    updatePageAccessType() {
        if (this.isCustomGraphType(this.selectedGraph)) {
            if (this.selectedGraph.publicAccess) {
                this.graphAccessType = PageAccessType.Public;
                this.graphAccessIcon = 'lock-open';
            } else {
                this.graphAccessType = PageAccessType.Private;
                this.graphAccessIcon = 'lock-closed';
            }
        } else {
            this.graphAccessIcon = null;
            this.graphAccessType = null;
        }
    }

    async saveScreenshot() {
        const imageBlob = await domtoimage.toBlob(this.customGraphContainer.nativeElement);
        const url = URL.createObjectURL(imageBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (this.selectedGraph as CustomGraph).displayName;
        a.click();
        URL.revokeObjectURL(url);
    }
}

export enum ExhaustGraphType {
    Cylinder = 'cylinder',
    Devitaion = 'deviation',
    Turbo = 'turbo',
}
